import {
  Alert,
  Anchor,
  Button,
  Center,
  Container,
  Divider,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { IconAlertCircle } from '@tabler/icons-react';

import { ApiError } from '../api/client';
import { authApi } from '../api/auth';
import { BaluarteLogo } from '../components/BaluarteLogo';
import { useCurrentUser, useLogin } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

interface LoginValues {
  email: string;
  password: string;
}

/**
 * Microsoft logo as inline SVG so we don't fetch from CDN.
 * Charter §5: no third-party network requests at runtime.
 * Source: Microsoft brand guidelines (four-square logo); colours
 * match official tile palette (#F25022, #7FBA00, #00A4EF, #FFB900).
 */
function MicrosoftLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export function LoginPage() {
  const { isAuthenticated, isLoading } = useCurrentUser();
  const login = useLogin();

  // Phase H0 #110: probe the SSO status endpoint. Stays disabled if
  // the backend doesn't have SSO_ENABLED=true + the four credentials.
  // Cached for the page lifetime — no need to re-poll.
  const ssoStatus = useQuery({
    queryKey: ['auth', 'sso-status'],
    queryFn: authApi.ssoStatus,
    staleTime: 5 * 60_000,
  });
  const ssoEnabled = ssoStatus.data?.enabled ?? false;

  const form = useForm<LoginValues>({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email'),
      password: (v) => (v.length >= 1 ? null : 'Password is required'),
    },
  });

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const errorMsg =
    login.error instanceof ApiError
      ? login.error.detail
      : login.error
        ? 'Sign-in failed. Please try again.'
        : null;

  return (
    <Container size={420} my={80}>
      <Center>
        <BaluarteLogo variant="stacked" height={48} />
      </Center>
      <Text c="dimmed" size="sm" ta="center" mt="md" tt="uppercase" style={{ letterSpacing: 2 }}>
        Client Relationship Management
      </Text>
      <Text c="dimmed" size="sm" ta="center" mt={20}>
        Sign in to continue
      </Text>

      <Paper withBorder shadow="sm" p="xl" mt={30} radius="md">
        <form onSubmit={form.onSubmit((values) => login.mutate(values))}>
          <Stack>
            {errorMsg && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                {errorMsg}
              </Alert>
            )}
            <TextInput
              label="Email"
              placeholder="you@baluarte-sci.com"
              autoComplete="email"
              required
              {...form.getInputProps('email')}
            />
            <PasswordInput
              label="Password"
              autoComplete="current-password"
              required
              {...form.getInputProps('password')}
            />
            <Button type="submit" fullWidth loading={login.isPending}>
              Sign in
            </Button>
            <Text c="dimmed" size="xs" ta="center">
              Forgot your password? <Anchor size="xs">Ask an administrator</Anchor>.
            </Text>
          </Stack>
        </form>

        {/* Phase H0 #110: SSO sign-in. Conditional on the backend
            reporting sso_enabled=true. When enabled, the local form
            above remains visible for break-glass admins listed in
            BALUARTE_LOCAL_ADMINS; everyone else uses this button. */}
        {ssoEnabled && (
          <>
            <Divider label="or" labelPosition="center" my="md" />
            <Button
              component="a"
              href="/auth/sso/login"
              fullWidth
              variant="default"
              leftSection={<MicrosoftLogo />}
            >
              Sign in with Microsoft
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
}
